import React from 'react';
import {
    Paper,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    Divider,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Refresh,
    ShowChart,
    BarChart as BarChartIcon,
    Layers,
    GridView,
    ViewList,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MEASURE_TYPES, MEASURE_CONFIG, getMeasureIcon } from '../../utils/measurementConfig';
import { MONTH_KEYS, getMonthName } from '../../utils/monthlyDataUtils';

function MonthlyControlBar({
    selectedYear,
    onYearChange,
    selectedMonth,
    onMonthChange,
    selectedZone,
    onZoneChange,
    availableZones,
    activeMeasure,
    onMeasureChange,
    chartType,
    onChartTypeChange,
    trendMode,
    onTrendModeChange,
    onRefresh,
    loading,
}) {
    const { t } = useTranslation();

    // Generate year range (e.g. current year - 4 to current year + 2)
    const currentYear = new Date().getFullYear();
    const yearsList = [];
    for (let y = currentYear - 5; y <= currentYear + 2; y++) {
        yearsList.push(y);
    }
    if (!yearsList.includes(selectedYear)) {
        yearsList.push(selectedYear);
        yearsList.sort();
    }

    const handlePrevYear = () => {
        onYearChange(selectedYear - 1);
    };

    const handleNextYear = () => {
        onYearChange(selectedYear + 1);
    };

    const isAllMonths = selectedMonth === 'all';

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                borderRadius: 2,
                backdropFilter: 'blur(8px)',
                transition: 'all 0.2s ease-in-out',
            }}
        >
            {/* Top row: Year, Month, Zone Selectors and Refresh */}
            <Grid container spacing={2} alignItems="center">
                {/* Year Selector with Stepper */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={t('common.previousYear', 'Previous Year')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handlePrevYear}
                                    disabled={loading}
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                >
                                    <ChevronLeft fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <FormControl fullWidth size="small">
                            <InputLabel id="year-select-label">{t('common.year', 'Year')}</InputLabel>
                            <Select
                                labelId="year-select-label"
                                id="year-select"
                                value={selectedYear}
                                label={t('common.year', 'Year')}
                                onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
                                disabled={loading}
                            >
                                {yearsList.map((yr) => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tooltip title={t('common.nextYear', 'Next Year')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleNextYear}
                                    disabled={loading}
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                >
                                    <ChevronRight fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Grid>

                {/* Month Selector */}
                <Grid size={{ xs: 12, sm: 6, md: 3.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="month-select-label">{t('common.month', 'Month')}</InputLabel>
                        <Select
                            labelId="month-select-label"
                            id="month-select"
                            value={selectedMonth}
                            label={t('common.month', 'Month')}
                            onChange={(e) => onMonthChange(e.target.value)}
                            disabled={loading}
                        >
                            <MenuItem value="all">
                                <em>{t('common.allMonths', 'All Months (12-Month Trend)')}</em>
                            </MenuItem>
                            {MONTH_KEYS.map((key, index) => {
                                const mNumber = index + 1;
                                return (
                                    <MenuItem key={key} value={mNumber}>
                                        {mNumber}. {t(`common.${key}`, getMonthName(mNumber, t))}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Zone Selector */}
                <Grid size={{ xs: 12, sm: 6, md: 3.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="zone-filter-select-label">{t('common.zone', 'Zone')}</InputLabel>
                        <Select
                            labelId="zone-filter-select-label"
                            id="zone-filter-select"
                            value={selectedZone}
                            label={t('common.zone', 'Zone')}
                            onChange={(e) => onZoneChange(e.target.value)}
                            disabled={loading}
                        >
                            <MenuItem value="all">
                                <em>{t('common.allZones', 'All Zones')}</em>
                            </MenuItem>
                            {availableZones.map((zoneName) => (
                                <MenuItem key={zoneName} value={zoneName}>
                                    {zoneName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Refresh and Action */}
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
                        <Tooltip title={t('common.refresh', 'Refresh Data')}>
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={onRefresh}
                                    disabled={loading}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        '&:hover': { bgcolor: 'primary.light', color: 'white' },
                                    }}
                                >
                                    <Refresh
                                        sx={{
                                            animation: loading ? 'spin 1s linear infinite' : 'none',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' },
                                            },
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Bottom row: Measurement Type Tabs and Chart View Switches */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                {/* Measurement Switcher */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('common.measures', 'Measures')}:
                    </Typography>

                    <ToggleButtonGroup
                        value={activeMeasure}
                        exclusive
                        onChange={(e, val) => {
                            if (val !== null) onMeasureChange(val);
                        }}
                        size="small"
                        aria-label="measurement selector"
                        sx={{
                            flexWrap: 'wrap',
                            '& .MuiToggleButton-root': {
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.8125rem',
                                textTransform: 'none',
                                fontWeight: 500,
                            },
                        }}
                    >
                        <ToggleButton value="ALL" aria-label="all measurements">
                            {t('common.allMeasures', 'All Metrics')}
                        </ToggleButton>

                        {MEASURE_TYPES.map((type) => {
                            const config = MEASURE_CONFIG[type];
                            const isSelected = activeMeasure === type;
                            return (
                                <ToggleButton
                                    key={type}
                                    value={type}
                                    aria-label={type}
                                    sx={{
                                        display: 'flex',
                                        gap: 0.75,
                                        alignItems: 'center',
                                        borderColor: isSelected ? `${config.color} !important` : undefined,
                                        bgcolor: isSelected ? config.lightBg : undefined,
                                    }}
                                >
                                    {getMeasureIcon(type, { fontSize: 18 })}
                                    <span>{t(config.translationKey, type)}</span>
                                    <Chip
                                        label={config.unit}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.7rem',
                                            bgcolor: isSelected ? config.color : 'action.selected',
                                            color: isSelected ? '#fff' : 'text.primary',
                                            fontWeight: 600,
                                        }}
                                    />
                                </ToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>
                </Box>

                {/* Chart Options / View Modes */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {isAllMonths ? (
                        <>
                            {/* Trend Mode Toggle (When All Months & specific measure): Compare Zones vs Compare Metrics */}
                            {selectedZone === 'all' && activeMeasure !== 'ALL' && (
                                <ToggleButtonGroup
                                    value={trendMode}
                                    exclusive
                                    onChange={(e, val) => {
                                        if (val !== null) onTrendModeChange(val);
                                    }}
                                    size="small"
                                >
                                    <ToggleButton value="byZone" sx={{ textTransform: 'none', py: 0.5, px: 1.25 }}>
                                        <Tooltip title={t('common.compareZones', 'Compare Zones')}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <GridView fontSize="small" />
                                                <Typography variant="caption">{t('common.compareZones', 'By Zone')}</Typography>
                                            </Box>
                                        </Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="byMetric" sx={{ textTransform: 'none', py: 0.5, px: 1.25 }}>
                                        <Tooltip title={t('common.compareMeasures', 'Compare Measures')}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ViewList fontSize="small" />
                                                <Typography variant="caption">{t('common.compareMeasures', 'By Metric')}</Typography>
                                            </Box>
                                        </Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}

                            {/* Chart Type Toggle: Line vs Area */}
                            <ToggleButtonGroup
                                value={chartType}
                                exclusive
                                onChange={(e, val) => {
                                    if (val !== null) onChartTypeChange(val);
                                }}
                                size="small"
                            >
                                <ToggleButton value="line" aria-label="line chart" sx={{ py: 0.5, px: 1.25 }}>
                                    <Tooltip title={t('common.lineChart', 'Line Chart')}>
                                        <ShowChart fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="area" aria-label="area chart" sx={{ py: 0.5, px: 1.25 }}>
                                    <Tooltip title={t('common.areaChart', 'Area Chart')}>
                                        <Layers fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </>
                    ) : (
                        /* Single Month View: Bar Chart vs Cards toggle */
                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={(e, val) => {
                                if (val !== null) onChartTypeChange(val);
                            }}
                            size="small"
                        >
                            <ToggleButton value="bar" aria-label="bar chart" sx={{ py: 0.5, px: 1.25 }}>
                                <Tooltip title={t('common.barChart', 'Bar Chart')}>
                                    <BarChartIcon fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="cards" aria-label="summary cards" sx={{ py: 0.5, px: 1.25 }}>
                                <Tooltip title={t('common.zoneSummaryCards', 'Summary Cards')}>
                                    <GridView fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}

export default MonthlyControlBar;
